// Cutover snapshots — one customer cutover grouping its migration jobs, with a
// scope, cutoff/go-live dates, a fingerprint-bound approval, a reconciliation
// manifest, go-live readiness, and a terminal lock. Loans are deliberately
// not a migration domain (external loans are never migrated).
import { formatDate } from '@/lib/orgFormat';
import React, { useEffect, useState } from 'react';
import {
  Camera, CheckCircle2, ChevronRight, Loader2, Lock, ShieldCheck, AlertTriangle,
  ArrowLeft, ClipboardCheck, Plus,
} from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { useHasPermission } from '@/rbac/useRbac';
import {
  listSnapshots, createSnapshot, getSnapshotManifest, getSnapshotReadiness,
  submitSnapshot, approveSnapshot, completeSnapshot, lockSnapshot, createMigration,
  MIGRATION_TARGETS,
  type MigrationSnapshot, type SnapshotManifest, type GoLiveReadiness,
} from '@/services/migrationService';

const fieldCls = 'rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

const snapStatusColor: Record<string, string> = {
  draft: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
  ready_for_approval: 'bg-amber-500/15 text-amber-600 dark:text-amber-300',
  approved: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300',
  completed: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300',
  completed_with_warnings: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  locked: 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-300',
};
const SnapChip = ({ s }: { s: string }) => (
  <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${snapStatusColor[s] || 'bg-muted'}`}>
    {s === 'locked' && <Lock className="mr-1 inline h-3 w-3" />}{s.replace(/_/g, ' ')}
  </span>
);

const CHECK_META: Record<string, { cls: string; label: string }> = {
  READY: { cls: 'text-emerald-600 dark:text-emerald-300', label: 'Ready' },
  NOT_READY: { cls: 'text-red-600 dark:text-red-300', label: 'Not ready' },
  WARNING: { cls: 'text-amber-600 dark:text-amber-300', label: 'Warning' },
  UNABLE_TO_VERIFY: { cls: 'text-slate-500', label: 'Unverified' },
  NOT_APPLICABLE: { cls: 'text-muted-foreground', label: 'N/A' },
};

// --------------------------------------------------------------- list + create
export const SnapshotList: React.FC<{ onOpen: (id: string) => void }> = ({ onOpen }) => {
  const canWrite = useHasPermission('migration:write');
  const [snaps, setSnaps] = useState<MigrationSnapshot[]>([]);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [source, setSource] = useState('');
  const [cutoff, setCutoff] = useState('');
  const [goLive, setGoLive] = useState('');
  const [domains, setDomains] = useState<Record<string, boolean>>({ employees: true, payroll: true, leave: true });

  const reload = () => listSnapshots().then(setSnaps).catch(() => setSnaps([]));
  useEffect(() => { reload(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const scope = { domains: Object.keys(domains).filter((d) => domains[d]) };
      const s = await createSnapshot({
        name: name || 'Customer cutover', source_system: source,
        cutoff_date: cutoff ? new Date(cutoff).toISOString() : undefined,
        go_live_date: goLive ? new Date(goLive).toISOString() : undefined,
        scope,
      });
      setCreating(false); setName(''); setSource('');
      reload(); onOpen(s.id);
    } finally { setBusy(false); }
  };

  return (
    <Card><CardContent className="p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Camera className="h-4 w-4 text-primary-500" /> Cutover snapshots
        </h2>
        {canWrite && (
          <Button variant="outline" size="sm" onClick={() => setCreating((c) => !c)}>
            <Plus className="mr-2 h-4 w-4" /> New snapshot
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        A snapshot groups one customer's migration jobs into a single cutover: choose the scope,
        run the imports, reconcile the counts, approve, then lock the history.
      </p>

      {creating && (
        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1.5"><span className="text-xs font-medium text-muted-foreground">Name</span>
              <input className={`${fieldCls} w-full`} value={name} placeholder="e.g. Fern Legacy HR Snapshot" onChange={(e) => setName(e.target.value)} /></label>
            <label className="space-y-1.5"><span className="text-xs font-medium text-muted-foreground">Source system</span>
              <input className={`${fieldCls} w-full`} value={source} placeholder="e.g. Sage HR" onChange={(e) => setSource(e.target.value)} /></label>
            <label className="space-y-1.5"><span className="text-xs font-medium text-muted-foreground">Cutoff date (balances as of)</span>
              <input type="date" className={`${fieldCls} w-full`} value={cutoff} onChange={(e) => setCutoff(e.target.value)} /></label>
            <label className="space-y-1.5"><span className="text-xs font-medium text-muted-foreground">Go-live date</span>
              <input type="date" className={`${fieldCls} w-full`} value={goLive} onChange={(e) => setGoLive(e.target.value)} /></label>
          </div>
          <div>
            <span className="text-xs font-medium text-muted-foreground">Migration scope</span>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5">
              {MIGRATION_TARGETS.map((t) => (
                <label key={t.key} className="flex items-center gap-1.5 text-sm text-foreground">
                  <input type="checkbox" checked={!!domains[t.key]}
                    onChange={(e) => setDomains((d) => ({ ...d, [t.key]: e.target.checked }))} />
                  {t.label}
                </label>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Loans from external lenders are never migrated — KaziPeople does not take over third-party loans.
            </p>
          </div>
          <Button size="sm" onClick={create} disabled={busy || !Object.values(domains).some(Boolean)}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />} Create snapshot
          </Button>
        </div>
      )}

      {snaps.length === 0 ? (
        !creating && <p className="text-sm text-muted-foreground">No cutover snapshots yet.</p>
      ) : (
        <div className="divide-y divide-border">
          {snaps.map((s) => (
            <button key={s.id} onClick={() => onOpen(s.id)} className="flex w-full items-center justify-between py-2.5 text-left hover:bg-accent rounded px-2 -mx-2">
              <div>
                <div className="text-sm font-medium text-foreground">{s.name}</div>
                <div className="text-xs text-muted-foreground">
                  {s.source_system || 'Unknown source'}
                  {s.cutoff_date ? ` · cutoff ${formatDate(s.cutoff_date)}` : ''}
                  {s.go_live_date ? ` · go-live ${formatDate(s.go_live_date)}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-3"><SnapChip s={s.status} /><ChevronRight className="h-4 w-4 text-muted-foreground" /></div>
            </button>
          ))}
        </div>
      )}
    </CardContent></Card>
  );
};

// -------------------------------------------------------------- detail view
export const SnapshotView: React.FC<{ id: string; onBack: () => void; onOpenJob: (id: string) => void }> = ({ id, onBack, onOpenJob }) => {
  const confirm = useConfirm();
  const canWrite = useHasPermission('migration:write');
  const canApprove = useHasPermission('migration:approve');
  const [m, setM] = useState<SnapshotManifest | null>(null);
  const [readiness, setReadiness] = useState<GoLiveReadiness | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jobName, setJobName] = useState('');
  const [jobTarget, setJobTarget] = useState('employees');

  const load = () => {
    getSnapshotManifest(id).then(setM).catch(() => setM(null));
    getSnapshotReadiness(id).then(setReadiness).catch(() => setReadiness(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setError(null); setBusy(key);
    try { await fn(); load(); }
    catch (e: any) { setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Something went wrong.'); }
    finally { setBusy(null); }
  };

  if (!m) return <div className="flex items-center gap-2 text-muted-foreground p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const snap = m.snapshot;
  const locked = snap.status === 'locked';
  const scopeDomains = snap.scope?.domains ?? [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All migrations</button>
        <SnapChip s={snap.status} />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2"><Camera className="h-5 w-5 text-primary-500" /> {snap.name}</h2>
        <p className="text-sm text-muted-foreground">
          {snap.source_system || 'Unknown source'}
          {snap.cutoff_date ? ` · cutoff ${formatDate(snap.cutoff_date)}` : ''}
          {snap.go_live_date ? ` · go-live ${formatDate(snap.go_live_date)}` : ''}
          {snap.fingerprint ? ` · fingerprint ${snap.fingerprint.slice(0, 12)}…` : ''}
        </p>
      </div>

      {error && <div className="rounded-md bg-danger/10 p-3 text-sm text-danger">{error}</div>}
      {locked && (
        <div className="rounded-md bg-indigo-500/10 p-3 text-sm text-indigo-700 dark:text-indigo-300 flex items-center gap-2">
          <Lock className="h-4 w-4" /> This snapshot is locked — history is frozen. Corrections go through a new snapshot.
        </div>
      )}

      {/* manifest */}
      <Card><CardContent className="p-5 space-y-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-primary-500" /> Reconciliation manifest</h3>
        {m.jobs.length === 0 ? <p className="text-sm text-muted-foreground">No migration jobs in this snapshot yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-muted-foreground">
                <th className="py-1.5 pr-3 font-medium">Migration</th><th className="pr-3 font-medium">Status</th>
                <th className="pr-3 font-medium text-right">Source</th><th className="pr-3 font-medium text-right">Created</th>
                <th className="pr-3 font-medium text-right">Updated</th><th className="pr-3 font-medium text-right">Skipped</th>
                <th className="pr-3 font-medium text-right">Errors</th><th className="pr-3 font-medium text-right">Warnings</th>
                <th className="font-medium text-center">Reconciled</th>
              </tr></thead>
              <tbody>
                {m.jobs.map((j) => (
                  <tr key={j.job_id} className="border-t border-border hover:bg-accent cursor-pointer" onClick={() => onOpenJob(j.job_id)}>
                    <td className="py-1.5 pr-3 font-medium text-foreground">{j.name}
                      <span className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[10px] uppercase text-muted-foreground">{j.target}</span></td>
                    <td className="pr-3 capitalize">{j.status.replace(/_/g, ' ')}</td>
                    <td className="pr-3 text-right">{j.source_rows}</td>
                    <td className="pr-3 text-right">{j.created}</td>
                    <td className="pr-3 text-right">{j.updated}</td>
                    <td className="pr-3 text-right">{j.skipped}</td>
                    <td className={`pr-3 text-right ${j.errors ? 'text-red-600 dark:text-red-300 font-medium' : ''}`}>{j.errors}</td>
                    <td className={`pr-3 text-right ${j.warnings ? 'text-amber-600 dark:text-amber-300 font-medium' : ''}`}>{j.warnings}</td>
                    <td className="text-center">{j.reconciled
                      ? <CheckCircle2 className="inline h-3.5 w-3.5 text-emerald-500" />
                      : <AlertTriangle className="inline h-3.5 w-3.5 text-red-500" />}</td>
                  </tr>
                ))}
                <tr className="border-t border-border font-medium text-foreground">
                  <td className="py-1.5 pr-3">Total</td><td className="pr-3" />
                  <td className="pr-3 text-right">{m.source_rows}</td>
                  <td className="pr-3 text-right">{m.created}</td>
                  <td className="pr-3 text-right">{m.updated}</td>
                  <td className="pr-3 text-right">{m.skipped}</td>
                  <td className="pr-3 text-right">{m.errors}</td>
                  <td className="pr-3 text-right">{m.warnings}</td>
                  <td className="text-center">{m.reconciled ? '✓' : '✗'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {!m.reconciled && (
          <p className="text-xs text-red-600 dark:text-red-300 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Some source rows are unaccounted for — a migration must never look successful when rows silently disappear.
          </p>
        )}
      </CardContent></Card>

      {/* add a job into this snapshot */}
      {canWrite && !locked && (
        <Card><CardContent className="p-5 flex flex-wrap items-end gap-3">
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Add a migration to this snapshot</span>
            <input className={`${fieldCls} w-64`} value={jobName} placeholder="e.g. Payroll history 24 months" onChange={(e) => setJobName(e.target.value)} />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Import into</span>
            <select className={`${fieldCls} w-56`} value={jobTarget} onChange={(e) => setJobTarget(e.target.value)}>
              {MIGRATION_TARGETS.filter((t) => scopeDomains.length === 0 || scopeDomains.includes(t.key)).map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </label>
          <Button size="sm" disabled={busy === 'addjob'}
            onClick={() => run('addjob', async () => {
              const j = await createMigration(jobName || 'Untitled migration', jobTarget, id);
              onOpenJob(j.id);
            })}>
            {busy === 'addjob' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Add migration
          </Button>
        </CardContent></Card>
      )}

      {/* go-live readiness */}
      <Card><CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary-500" /> Go-live readiness</h3>
          {readiness && (
            <span className={`rounded px-2 py-0.5 text-xs font-medium ${readiness.ready ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300' : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'}`}>
              {readiness.ready ? 'Ready for cutover' : 'Not ready yet'}
            </span>
          )}
        </div>
        {!readiness ? <p className="text-sm text-muted-foreground">Readiness could not be loaded.</p> : (
          <ul className="space-y-1.5">
            {readiness.checks.map((c) => (
              <li key={c.key} className="flex items-start gap-2 text-sm">
                <span className={`mt-0.5 text-xs font-semibold w-20 shrink-0 ${CHECK_META[c.status]?.cls || ''}`}>{CHECK_META[c.status]?.label || c.status}</span>
                <span className="text-foreground">{c.label}</span>
                {c.detail && <span className="text-xs text-muted-foreground">— {c.detail}</span>}
              </li>
            ))}
          </ul>
        )}
        <p className="text-[11px] text-muted-foreground">
          Company configuration checks (departments, payroll legal entity, leave policies, approvers) live in Getting Started — both must be green before cutover.
        </p>
      </CardContent></Card>

      {/* lifecycle actions */}
      {!locked && (
        <Card><CardContent className="p-5 flex flex-wrap items-center gap-3">
          {snap.status === 'draft' && canWrite && (
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run('submit', () => submitSnapshot(id))}>
              {busy === 'submit' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardCheck className="mr-2 h-4 w-4" />} Submit for approval
            </Button>
          )}
          {snap.status === 'ready_for_approval' && canApprove && (
            <Button size="sm" disabled={!!busy} onClick={() => run('approve', () => approveSnapshot(id))}>
              {busy === 'approve' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Approve snapshot
            </Button>
          )}
          {snap.status === 'approved' && canApprove && (
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run('complete', () => completeSnapshot(id))}>
              {busy === 'complete' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Mark cutover complete
            </Button>
          )}
          {(snap.status === 'completed' || snap.status === 'completed_with_warnings') && canApprove && (
            <Button size="sm" variant="outline" disabled={!!busy}
              onClick={async () => {
                if (await confirm({
                  title: 'Lock snapshot',
                  message: 'Lock this snapshot? The migrated history becomes permanent — it stays visible and reportable, but nothing in it can change. Corrections after locking need a new snapshot.',
                  confirmLabel: 'Lock', danger: true,
                })) run('lock', () => lockSnapshot(id));
              }}>
              {busy === 'lock' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />} Lock snapshot
            </Button>
          )}
          <span className="text-xs text-muted-foreground">
            {snap.status === 'draft' && 'Every job must be validated with 0 errors before submitting.'}
            {snap.status === 'ready_for_approval' && (canApprove ? 'Approving binds a fingerprint over exactly what was validated.' : 'Awaiting an approver.')}
            {snap.status === 'approved' && 'Execute every job, then mark the cutover complete.'}
            {(snap.status === 'completed' || snap.status === 'completed_with_warnings') && 'Locking freezes the migrated history permanently.'}
          </span>
        </CardContent></Card>
      )}
    </div>
  );
};
